/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** @sfdc-extension-file SFDC_EXT_PRODUCT_CONTENT */
import { Suspense } from 'react';
import { Await } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { SizeGuideModalContent } from '@/components/info-modal/renderers/size-guide-modal-content';
import { useProductContentData } from '@/extensions/product-content/context/product-content-data-context';
import type { SizeGuideData } from '@/extensions/product-content/lib/api/product-content.server';

/**
 * Body for the PDP "Size Chart" tab — the dimensional sizing breakdown.
 *
 * Renders the same content as the Size Chart modal (`SizeGuideModalContent`, shared with the inline
 * link next to the size row) so a merchant authors the chart once and both surfaces stay in sync.
 * Falls back to the localized "coming soon" copy when a product has no size guide, since the tab
 * itself is rendered by the core PDP and cannot know in advance whether content exists.
 */
export default function SizeChartContentTarget() {
    const ctx = useProductContentData();
    const sizeGuidePromise = ctx?.sizeGuidePromise;

    if (!sizeGuidePromise) return <SizeChartUnavailable />;

    return (
        <Suspense fallback={<SizeChartSkeleton />}>
            <Await resolve={sizeGuidePromise} errorElement={<SizeChartUnavailable />}>
                {(sizeGuide: SizeGuideData | null) => {
                    const hasContent = Boolean(
                        sizeGuide?.htmlContent?.html?.trim() ||
                            sizeGuide?.chart?.rows?.length ||
                            sizeGuide?.howToMeasure?.length
                    );
                    if (!sizeGuide || !hasContent) return <SizeChartUnavailable />;
                    return (
                        <div className="space-y-4">
                            <SizeGuideModalContent sizeGuideData={sizeGuide} />
                        </div>
                    );
                }}
            </Await>
        </Suspense>
    );
}

function SizeChartSkeleton() {
    return (
        <div className="space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-24 w-full" />
        </div>
    );
}

function SizeChartUnavailable() {
    const { t } = useTranslation('extProductContent');
    return <p className="text-sm text-muted-foreground">{t('contentComingSoon')}</p>;
}
